import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
//common components
import { AppState } from "../config/redux/reducers";
import AppConstants from "../constants";
import GridWrapper from "../common/GridWrapper";
import Loader from "../common/Loader";
import CustomSnackbar from "../common/CustomSnackbar";
//redux
import { userLogout } from "../Login/redux/loginSlice";
import { fetchDriversFilterChipsDisplayCount, fetchDriversListByFilter } from './redux/driversSlice';
//Material UI Components
import { CellParams, SortModel } from "../common/GridWrapper/DataTable";
import { Grid, IconButton, SvgIcon, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
//styles
import { useStyles } from "./drivers.styles";
//assets
import { ReactComponent as checkIcon } from './../assets/icons/circle-checked-icon.svg';
import viewIcon from './../assets/icons/Eye.svg';
//other components
import { createChipsCountFilterPayload, driversDataTransform, createFilterPayload, getDriverStatusField } from "../mocks/drivers/response.transforms";
import DriverDetails from './DriverDetails';
import ExportInitiatedDialogProvider from 'ExportIntiaiedDialog/ExportInitiatedDialogProvider';
import { exportToExcel } from 'ExportIntiaiedDialog/redux/exportInitiatedDialogSlice';
import DeliveryConstants from "constants/SharedConstants/DeliveryConstants";


interface DriversProps {
    match?: any;
    location?: any;
}

const Drivers = (props: DriversProps) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { tableLoading, driversFilterChipsDisplayCount, driversData, driversFiltersDropdowns, userUpdateStatus, loading, error, errorCode, excelLoading, exportError, assignmentModeStatus, releaseDriverStatus } = useSelector(
        (state: AppState) => state.drivers
    )
    const { countryCode, hubCode } = useSelector(
        (state: AppState) => state.common
    );

    const onLoadRef = useRef(true);
    const [activeOrdersFilterChip, setActiveOrdersFilterChip] = useState(AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.DEFAULT_FILTER_CHIP);

    const [isOrderSearched, setIsOrderSearched] = useState(false);
    const [ordersSearchValue, setOrdersSearchValue] = useState("");
    const [isTabChange, setIsTabChange] = useState(false);
    const [ordersTableWrapperData, setOrdersTableWrapperData] = useState({
        ...AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA, HeaderData: AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.TABLE_WRAPPER_DATA.HeaderData.map((header: any) => {
            let newHeader = { ...header };
            if (newHeader.field === 'driverName') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography className="idLink">{params.value}</Typography>
                )
            }
            if (newHeader.field === 'status') {
                newHeader.renderCell = (params: any) => (
                    params.value ?
                        <Grid className={classes.statusHeaderContainer} container alignItems="center">
                            <Typography className={['tag', getDriverStatusField('color', params.value),].join(" ")}>{params.value ? params.value : ""}</Typography>
                        </Grid>
                        : <></>
                );
            }
            if (newHeader.field === 'hubCode') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography className="multiple">{params && params.value && params.value.join(" , ")}</Typography>
                );
            }
            if (newHeader.field === 'hubNames') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography className="multiple">{params && params.value && params.value.join(" , ")}</Typography>
                );
            }
            if (newHeader.field === 'actions') {
                newHeader.renderCell = (params: CellParams) => (
                    <Typography className="actionsLink"><img src={viewIcon} alt="view" /></Typography>
                )
            }
            return newHeader;
        }),
    });

    const [ordersActiveFilters, setOrdersActiveFilters] = useState({ ...ordersTableWrapperData.defaultFiltersObj });
    const [ordersListToShow, setOrdersListToShow] = useState([]);
    const [isDisplayCount, setIsDisplayCount] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [openDriverProfilePopup, setOpenDriverProfilePopup] = useState(false);
    const [driverData, setDriverData] = useState({});
    const [renderExcelProgress, setRenderExcelProgress] = useState(false);
    const [tabValue, setTabValue] = useState(AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.TAB_VALUES.DRIVER_PROFILE);
    const snackbarType = useRef(AppConstants.SNACKBAR.TYPES.SUCCESS);
    const snackbarMessage = useRef("");

    const ordersSearchFilterHandler = (data: { [key: string]: string }, updatedSearchValue: string, filters: any) => {
        const activeFilters = _.cloneDeep(filters);
        onLoadRef.current = false;
        setOrdersSearchValue(updatedSearchValue);
        setIsOrderSearched(false);
        if (updatedSearchValue) {
            setIsOrderSearched(true);
            for (const searchKey in data) {
                if (data[searchKey]) {
                    activeFilters.searchAttribute = {
                        key: searchKey,
                        value: data[searchKey]
                    }
                    ordersFilterCallback(activeFilters);
                    return;
                }
            }
        }
    };

    const handleExcelExport = useCallback(
        (filters: any) => {
            if (filters) {
                const payloadFilters = _.cloneDeep(filters);
                payloadFilters.pageSize = ordersTableWrapperData.loadMore.pageSize;
                const payload = createFilterPayload(payloadFilters, driversData, false, countryCode, activeOrdersFilterChip);
                dispatch(
                    exportToExcel({
                        payload: payload,
                        url: DeliveryConstants.REPORT_URL.DRIVERS,
                    })
                );
            }
            setRenderExcelProgress(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, driversData, countryCode, activeOrdersFilterChip]
    )

    const handleOrdersHeaderButtonClick = useCallback(
        (btnObj: any) => {
            if (btnObj && btnObj.type) {
                switch (btnObj.type) {
                    case 'excel':
                        handleExcelExport(ordersActiveFilters);
                        break;
                    default:
                        break;
                }
            }
        },
        [ordersActiveFilters, handleExcelExport]
    );

    const getFilterChipsCount = useCallback(
        (payload: any) => {
            setIsDisplayCount(false);
            dispatch(
                fetchDriversFilterChipsDisplayCount({
                    payload: payload
                })
            );
            setIsDisplayCount(true);
        },
        [dispatch],
    )

    const ordersFilterCallback = useCallback(
        (filters: any, isLoadMore?: boolean, isChipFilterClicked?: boolean, selectedChip?: any, isChipClicked?: boolean) => {
            if (filters) {
                let payloadFilters = _.cloneDeep(filters);
                payloadFilters.pageSize = ordersTableWrapperData.loadMore.pageSize;
                if (!isChipFilterClicked) {
                    selectedChip = activeOrdersFilterChip;
                }
                const payload = createFilterPayload(payloadFilters, driversData, isLoadMore, countryCode, selectedChip);
                let payloadFilters2 = _.cloneDeep(filters);
                payloadFilters2.pageSize = ordersTableWrapperData.loadMore.pageSize;
                const payloadForCount = createChipsCountFilterPayload(payloadFilters2, driversData, isLoadMore, countryCode);
                getFilterChipsCount(payloadForCount);
                if(isLoadMore){
                    setIsTabChange(false);
                  }
                dispatch(
                    fetchDriversListByFilter({
                        filters: payload
                    })
                );
                setOrdersActiveFilters(filters);
            }
            setRenderExcelProgress(false);
        },
        [driversData, ordersTableWrapperData, activeOrdersFilterChip, dispatch, countryCode, getFilterChipsCount],
    );

    const getFilterChips = () => {
        const filterChips = AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.FILTER_CHIPS;
        return filterChips.map((chip: any, index: number) => (
            <Grid key={`chipFilterItem-${index}`} item className={`chipFilterItem ${activeOrdersFilterChip === chip.code ? 'active' : ''}`} onClick={() => handleChipFilter(chip, ordersActiveFilters)}>
                <Grid container className="chipContainer">
                    <Grid item className="chipItem name">
                        <Typography>{chip.value}</Typography>
                    </Grid>
                    <Grid item className="chipItem count">
                        <Typography>{driversFilterChipsDisplayCount.driverStatusCount[chip.code]}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        ))
    };

    const handleConsignmentCellClick = useCallback(
        (params: CellParams) => {
            if (params.field === 'driverName') {
                const selectedRow: any = params.rowData;
                const selectedDriver = driversData.elements.filter((item: any) => item.driverName === selectedRow.driverName);
                setDriverData(selectedDriver.length ? selectedDriver[0] : {});
                setOpenDriverProfilePopup(true);
                setTabValue(AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.TAB_VALUES.DRIVER_PROFILE);
            }
            if (params.field === 'actions') {
                const selectedRow: any = params.rowData;
                const selectedDriver = driversData.elements.filter((item: any) => item.driverName === selectedRow.driverName);
                setDriverData(selectedDriver.length ? selectedDriver[0] : {});
                setOpenDriverProfilePopup(true);
                setTabValue(AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.TAB_VALUES.ACTIVITY_LOG);
            }
        },
        [driversData]
    );

    const openSnackbarPopup = (msg: string, type: string) => {
        snackbarMessage.current = msg;
        snackbarType.current = type;
        setOpenSnackbar(true);
    }

    const handleSnackbarClose = useCallback(
        () => {
            setOpenSnackbar(false);
        },
        [],
    );

    const handleSnackbarExited = useCallback(
        () => {
            if (errorCode === AppConstants.DRIVERS_CONSTANTS.RESPONSE_CONSTANTS.ERROR_CODES.SESSION_TIMEOUT) {
                dispatch(
                    userLogout()
                );
            }
        },
        [dispatch, errorCode]
    );

    const handleProgressClose = useCallback(
        () => {
            setRenderExcelProgress(false);
        },
        [],
    );

    const handleLoadMoreDisableState = useCallback(
        () => {
            let loadMoreState = ordersTableWrapperData.loadMore;
            loadMoreState.disabled = driversData.totalPages ? driversData.pageNumber + 1 >= driversData.totalPages : true;
            setOrdersTableWrapperData({ ...ordersTableWrapperData, loadMore: loadMoreState });
        },
        [ordersTableWrapperData, driversData]
    );

    const handleChipFilter = useCallback(
        (chip: any, activeFilters: any) => {
            let filters = { ...activeFilters };
            setIsTabChange(true);
            onLoadRef.current = false;
            setIsOrderSearched(false);
            if (chip.code !== activeOrdersFilterChip) {
                let isChipClicked = true;
                setActiveOrdersFilterChip(chip.code);
                ordersFilterCallback(filters, false, true, chip.code, isChipClicked);
            }
            setRenderExcelProgress(false);
        },
        [ordersFilterCallback, activeOrdersFilterChip]
    )

    const closeConsignmentDetailsPopup = useCallback(
        () => {
            setOpenDriverProfilePopup(false);
        },
        [],
    );

    useEffect(() => {
        if (userUpdateStatus) {
            ordersFilterCallback(ordersActiveFilters)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userUpdateStatus])

    useEffect(() => {
        if (assignmentModeStatus) {
            ordersFilterCallback(ordersActiveFilters)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignmentModeStatus])

    useEffect(() => {
        if (releaseDriverStatus) {
            ordersFilterCallback(ordersActiveFilters);
            setOpenDriverProfilePopup(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [releaseDriverStatus])

    useEffect(() => {
        if (error) {
            openSnackbarPopup(error, AppConstants.SNACKBAR.TYPES.ERROR);
        }
    }, [error])

    useEffect(() => {
        if (isOrderSearched && isDisplayCount) {
            const chips = Object.keys(driversFilterChipsDisplayCount.driverStatusCount).filter((key: string) => driversFilterChipsDisplayCount.driverStatusCount[key] > 0);
            let selectedChip;
            let selectedChipValue;
            if (chips.length === 2) {
                selectedChip = chips.filter((key: string) => key !== AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP);
                selectedChipValue = selectedChip[0];
            } else {
                selectedChip = chips.filter((key: string) => key === AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.ALL_FILTER_CHIP);
                selectedChipValue = selectedChip[0];
            }
            handleChipFilter({ code: chips.length ? selectedChipValue : activeOrdersFilterChip }, ordersActiveFilters)
        } else {
            setOrdersListToShow(driversDataTransform(driversData) as any);
            handleLoadMoreDisableState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driversFilterChipsDisplayCount, driversData]);

    useEffect(() => {
        const ordersHeaderData = ordersTableWrapperData.HeaderData.map((header: any) => {
            if (header.filterObj) {
                if (header.filterObj.fieldName === 'hubCode') {
                    header.filterObj.items = driversFiltersDropdowns.hubCodeList || [];
                }
                if (header.filterObj.fieldName === 'hubName') {
                    header.filterObj.items = driversFiltersDropdowns.hubNameList || [];
                }
                if (header.filterObj.fieldName === 'status') {
                    header.filterObj.items = driversFiltersDropdowns.statusList || [];
                }
                if (header.filterObj.fieldName === 'vehicle') {
                    header.filterObj.items = driversFiltersDropdowns.vehicleList || [];
                }
                if (header.filterObj.fieldName === 'vendor') {
                    header.filterObj.items = driversFiltersDropdowns.vendorList || [];
                }
                if (header.filterObj.fieldName === 'modes') {
                    header.filterObj.items = driversFiltersDropdowns.modes || [];
                }
            }
            return header;
        });
        const hubsToAdd = hubCode.map((hub: string) => ({
            name: hub,
            value: hub
        }));
        let updatedFilters: any = {};

        setOrdersTableWrapperData({ ...ordersTableWrapperData, HeaderData: ordersHeaderData });
        setActiveOrdersFilterChip(AppConstants.DRIVERS_CONSTANTS.CONSIGNMENTS_DATA.DEFAULT_FILTER_CHIP);
        setIsOrderSearched(false);
        updatedFilters = { ...ordersActiveFilters, hubCode: { ...ordersActiveFilters.hubCode, value: hubsToAdd } };
        if (!onLoadRef.current) {
            ordersFilterCallback(updatedFilters);
        }
        onLoadRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubCode]);

    useEffect(() => {
        if (ordersTableWrapperData && ordersTableWrapperData.HeaderData) {
            let headerData = ordersTableWrapperData.HeaderData.map((header: any) => {
                if (header.filterObj) {
                    if (header.filterObj.fieldName === 'hubCode') {
                        header.filterObj.items = driversFiltersDropdowns.hubCodeList || [];
                    }
                    if (header.filterObj.fieldName === 'hubName') {
                        header.filterObj.items = driversFiltersDropdowns.hubNameList || [];
                    }
                    if (header.filterObj.fieldName === 'status') {
                        header.filterObj.items = driversFiltersDropdowns.statusList || [];
                    }
                    if (header.filterObj.fieldName === 'vehicle') {
                        header.filterObj.items = driversFiltersDropdowns.vehicleList || [];
                    }
                    if (header.filterObj.fieldName === 'vendor') {
                        header.filterObj.items = driversFiltersDropdowns.vendorList || [];
                    }
                    if (header.filterObj.fieldName === 'modes') {
                        header.filterObj.items = driversFiltersDropdowns.modes || [];
                    }
                }
                return header;
            });
            setOrdersTableWrapperData({ ...ordersTableWrapperData, HeaderData: headerData });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driversFiltersDropdowns]);

    return (
        <div className={classes.root}>
            {loading && <Loader></Loader>}
            <Grid className={classes.container} container>
                <Grid className="planningItem content" item>
                    <Grid container className={classes.tabPanelContainer}>
                        <Grid item className="panelItem chipFilter">
                            <Grid container className="chipsContainer">
                                {
                                    getFilterChips()
                                }
                            </Grid>
                        </Grid>
                        <Grid item className="panelItem gridWrapper">
                            <GridWrapper
                                showHeader={true}
                                checkboxSelection={false}
                                headerData={ordersTableWrapperData.HeaderData}
                                rowData={ordersListToShow}
                                isTabChange={isTabChange}
                                loading={loading ? false : tableLoading}
                                searchTypes={ordersTableWrapperData.SEARCH_TYPES}
                                defaultFilter={ordersTableWrapperData.defaultFiltersObj}
                                activeFilter={ordersActiveFilters}
                                headerButtons={ordersTableWrapperData.headerButtons}
                                title={ordersTableWrapperData.title}
                                disableSelectionOnClick={true}
                                sortModel={ordersTableWrapperData.sortModel as SortModel}
                                searchValue={ordersSearchValue}
                                loadMore={{ ...ordersTableWrapperData.loadMore, rowCount: driversData.totalElements || 0 }}
                                headerBtnClick={handleOrdersHeaderButtonClick}
                                searchCallback={ordersSearchFilterHandler}
                                filterGrid={ordersFilterCallback}
                                onCellClick={handleConsignmentCellClick}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {!error && renderExcelProgress
                   ? excelLoading && (
                       <Grid container className={classes.excelDownloadStatus}>
                         <Grid item>
                            <Typography className={classes.excelDownloadStatusText}>
                             {AppConstants.EXPORT_FILE_CONSTANT.DOWNLOAD_INITIATED}
                             </Typography>
                         </Grid>
                         <Loader />
                     </Grid>
                ) : (
                        exportError === "INTERNAL_SERVER_ERROR" && renderExcelProgress ? (
                            <Grid container className={classes.excelDownloadError}>
                                <Grid item>
                                    <Typography className={classes.excelDownloadStatusText}>
                                        Error Downloading the file. Try Again!!
                                    </Typography>
                                </Grid>
                                <Grid item style={{ display: "flex" }}>
                                    <IconButton aria-label="close" className={classes.close} onClick={handleProgressClose}>
                                        <CloseIcon className={classes.closeIcon} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ) : null)}
                <ExportInitiatedDialogProvider />       
                <DriverDetails open={openDriverProfilePopup} defaultTab={tabValue} user={driverData} closePopup={closeConsignmentDetailsPopup}></DriverDetails>

                <CustomSnackbar open={openSnackbar} handleClose={handleSnackbarClose} onExited={handleSnackbarExited} autoHideDuration={AppConstants.SNACKBAR.AUTO_HIDE_TIMEOUT} message={snackbarMessage.current} type={snackbarType.current} />
            </Grid>
        </div >
    );
}

export default Drivers;